import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  Paper,
  Box,
  Alert,
  Divider,
  Chip,
  Checkbox,
  FormControlLabel,
  ButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { format } from 'date-fns';
import { formatNumber, formatShortDate } from '../../utils/formatters';
import { apiGet } from '../../utils/apiClient';
import CurrencyInput, { formatWithSpaces } from '../common/CurrencyInput';
import PaymentMethodSelect from '../common/PaymentMethodSelect';
import ProofUploadField from '../common/ProofUploadField';
import { ExistingProofList, toProofIds } from './PaymentProofCell';
import useDocumentActions from '../../hooks/useDocumentActions';
import DocumentPreviewDialog from '../documents/DocumentPreviewDialog';
import { useTenant } from '../../contexts/TenantContext';
import { partialPaymentInfo, seedInterestWhtLegs, apportionByWeight } from '../../utils/paymentHelpers';

/**
 * Unified match dialog used by Payments / Combined / WHT flows.
 *
 * Sections rendered:
 *   - Header (always) — plus the T3-105 WHT panel when an interest row carries withholding tax
 *   - Payment metadata (always)
 *   - Allocation — whenever a LEG PAIR is active: Combined (interest/capital), or an interest
 *     match with the T3-105 WHT tick-box ticked (interest/tax). ONE implementation serves both;
 *     see `legPair`. Do not add a second allocation block.
 *   - Variance — only when |cash - expected| >= 0.01, where `expected` includes the WHT leg when
 *     it is being recorded (otherwise every on-schedule combined match reports a false variance)
 *
 * The dialog does NOT pick the endpoint. Callers receive a structured payload
 * via onSubmit and route to the right endpoint themselves.
 *
 *   matchType = 'payment'  → row narration is Interest Payment / Capital Payment / Adhoc Capital Withdrawal
 *   matchType = 'combined' → row narration is Combined Payment (interest + capital split)
 *   matchType = 'wht'      → WHT payment
 */
const MatchDialog = ({
  open,
  onClose,
  onSubmit,
  submitting,
  matchType = 'payment',
  scheduleRow,
  currencyCode,
  contractReference,
  userReference,
  investorName,
  /* T117 multi-currency: when the contract settles in a different currency than it accrues
     in, the user enters the foreign settlement amount + dealt FX rate, and the contract-
     currency equivalent (= settlement * rate) is what feeds the existing variance/allocation
     logic. Pass settlementCurrencyCode (e.g. 'BWP') to enable; null/equal => legacy behaviour.
     currencyCode is the contract (accrual) currency. */
  settlementCurrencyCode,
  /* WHT-only extras shown in the header for context */
  whtContext, // { gross_interest, wht_rate, wht_amount } when matchType === 'wht'
  /* Mode: 'match' (default) creates a new match; 'reapportion' edits an
     existing match in place. In reapportion mode the cash amount and all
     payment metadata are locked read-only; only the allocation and variance
     reason are editable. */
  mode = 'match',
  initialPayment, // { payment_amount, payment_date, payment_reference, payment_method, notes } pre-fill
  initialAllocation, // { allocated_interest, allocated_capital } pre-fill
  initialVarianceReason, // string pre-fill
}) => {
  const { isWorkflowDocumentRequired } = useTenant();
  // Feature 30 Phase 4: a fee match has its own workflow-document code, so a tenant can require
  // proof for fee settlements independently of interest ones.
  const workflowCode = matchType === 'wht' ? 'wht_match'
    : (matchType === 'fee' ? 'fee_match' : 'payment_match');

  const isReapportion = mode === 'reapportion';
  const isView = mode === 'view';
  // T117: multi-currency when a settlement currency is supplied and differs from the contract ccy.
  const isMultiCurrency = !!settlementCurrencyCode && settlementCurrencyCode !== currencyCode;
  // In view mode every input is read-only; in reapportion mode only the
  // cash/date/reference/method/notes/proof are locked but the allocation +
  // variance reason are editable.
  const lockMetadata = isReapportion || isView;
  const lockAllocation = isView;
  const expectedInterest = parseFloat(scheduleRow?.interest_amount) || 0;
  const expectedCapital = parseFloat(scheduleRow?.capital_amount) || 0;
  const scheduledTotal = parseFloat(scheduleRow?.scheduled_amount)
    || parseFloat(scheduleRow?.payment_amount)
    || (expectedInterest + expectedCapital);

  /* T3-111 — what is STILL OWED, which is what this dialog must be about.
     T3-103 keeps a partially-settled line in the queue so the shortfall can be topped up, but
     until T3-111 nothing projected how much had already been received, so the dialog opened on
     the FULL scheduled amount. An operator accepting that default on a part-paid line re-paid
     the cash already banked (dev Med-SZ-2026-0001: 6,000.00 of a 6,934.93 line already settled,
     so the dialog would have proposed 6,934.93 against 934.93 genuinely owed).
     On an untouched row matched_amount is 0 and this IS the scheduled total, so the ordinary
     path is unchanged. */
  const partial = partialPaymentInfo(scheduleRow || {});
  const expectedTotal = partial.isPartial ? partial.outstanding : scheduledTotal;

  /* T3-105 — WHT context, supplied by fn_payment_get_schedule_rows.
     Presence test is on wht_amount, NOT wht_rate_applied: the rate is stored even when it is 0,
     so it is not a valid presence signal (same reasoning as AmortizationScheduleTable). */
  const whtAmount = parseFloat(scheduleRow?.wht_amount) || 0;
  const hasWht = Math.abs(whtAmount) > 0;
  const whtGrossUp = scheduleRow?.wht_gross_up === true || scheduleRow?.wht_gross_up === 'true';
  const whtAlreadyPaid = scheduleRow?.wht_paid === true || scheduleRow?.wht_paid === 'true';
  /* Gross comes from the DB and is NEVER computed as interest + wht: on a gross-up row the payer
     absorbs the tax so net already equals gross, and summing would over-state it by the tax. */
  const grossInterest = parseFloat(scheduleRow?.gross_interest_amount) || expectedInterest;
  const whtRate = scheduleRow?.wht_rate_applied;
  /* View mode only: the WHT leg as actually recorded — amount, date and reference — resolved by the
     caller from the matched-WHT list. Absent when the tax has not been remitted. */
  const whtSettlement = scheduleRow?._wht_settlement || null;
  const whtRemittanceDiff = whtSettlement
    ? +((parseFloat(whtSettlement.amount) || 0) - whtAmount).toFixed(2)
    : 0;

  // Form state
  const [paymentAmount, setPaymentAmount] = useState(expectedTotal);
  /* Has the operator typed their own cash figure? The WHT tick-box adjusts the amount field, but it
     must never DESTROY a deliberate entry: on a short pay of 1,500 against a 1,613.70 line, ticking
     the box previously overwrote it with the scheduled total, silently discarding what the user had
     just typed. Once dirty, ticking shifts the amount by the tax DELTA instead of replacing it. */
  const [cashDirty, setCashDirty] = useState(false);
  // Default the date to the scheduled payment date if present, else today.
  // Users typically capture cash on or near the scheduled date, so the
  // schedule date is a better starting point than 'now'.
  const initialDate = scheduleRow?.payment_date
    ? format(new Date(scheduleRow.payment_date), 'yyyy-MM-dd')
    : format(new Date(), 'yyyy-MM-dd');
  const [paymentDate, setPaymentDate] = useState(initialDate);
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('EFT');
  const [notes, setNotes] = useState('');
  /* T3-129: a LIST of proof documents, not one. A match routinely needs several supporting
     documents (bank advice, SWIFT confirmation, remittance screenshot) and this used to accept
     exactly one. The parent (PaymentMatching) uploads them and sends the resulting id array. */
  const [proofFiles, setProofFiles] = useState([]);
  const [proofError, setProofError] = useState('');

  // Apportionment state (Combined only)
  const [allocatedInterest, setAllocatedInterest] = useState(expectedInterest);
  const [allocatedCapital, setAllocatedCapital] = useState(expectedCapital);

  // Variance reason
  const [varianceReason, setVarianceReason] = useState('');

  /* T3-105 — record the withholding-tax remittance alongside the interest payment.
     Defaults OFF, deliberately: paying tax is a deliberate act and must be chosen consciously,
     never recorded by a user failing to untick something. Off also means the DEFAULT path is
     byte-for-byte today's interest-only behaviour. */
  const [whtIncluded, setWhtIncluded] = useState(false);

  /* Proof of payment. T3-105: ONE document covers BOTH legs — a combined match is one cash
     movement from the operator's side, so demanding two uploads of the same payment advice would be
     nonsense. Tenants commonly have payment_match OPTIONAL but wht_match REQUIRED, so ticking the
     box can make a proof mandatory that was not before; the label reflects that as it happens. */
  const proofRequired = isWorkflowDocumentRequired(workflowCode)
    || (whtIncluded && isWorkflowDocumentRequired('wht_match'));
  const proofLabelBase = matchType === 'wht'
    ? 'Proof of WHT Payment'
    : matchType === 'fee'
      ? 'Proof of Fee Payment'
      : (whtIncluded ? 'Proof of Payment (covers both the interest and the tax leg)' : 'Proof of Payment');
  const proofLabel = `${proofLabelBase} ${proofRequired ? '(Required)' : '(Optional)'}`;

  /* T3-129: stage-time validation, mirroring the server's own limits (routes/document-review.js:
     10 MB, and the pdf/jpg/jpeg/png/doc/docx/xls/xlsx allowlist). Rejecting here rather than at
     upload keeps the fail-loud abort below for genuine transport failures, instead of firing it on
     something the user could have been told about immediately. */
  const MAX_PROOF_FILES = 10;
  const MAX_PROOF_BYTES = 10 * 1024 * 1024;
  const PROOF_EXT_RE = /\.(pdf|jpe?g|png|docx?|xlsx?)$/i;

  const validateProofFile = (f) => {
    if (!PROOF_EXT_RE.test(f.name)) return `"${f.name}" is not an accepted type — attach a PDF, image, Word or Excel file.`;
    if (f.size > MAX_PROOF_BYTES) return `"${f.name}" is larger than the 10 MB limit.`;
    return null;
  };

  /* Accumulate the newly picked batch onto what is already staged (FileDropZone emits only the
     new batch — a file input reports just its own last selection). Deduped on
     name|size|lastModified so re-picking the same file cannot double it up. */
  const acceptProofFiles = (batch) => {
    setProofError('');
    const keyOf = (f) => `${f.name}|${f.size}|${f.lastModified}`;
    setProofFiles((prev) => {
      const seen = new Set(prev.map(keyOf));
      const next = [...prev];
      const errors = [];
      for (const f of batch) {
        const msg = validateProofFile(f);
        if (msg) { errors.push(msg); continue; }
        if (seen.has(keyOf(f))) continue;
        if (next.length >= MAX_PROOF_FILES) {
          errors.push(`At most ${MAX_PROOF_FILES} documents can be attached.`);
          break;
        }
        seen.add(keyOf(f));
        next.push(f);
      }
      if (errors.length) setProofError(errors.join(' '));
      return next;
    });
  };

  const removeProofFileAt = (index) => {
    setProofError('');
    setProofFiles((prev) => prev.filter((_, i) => i !== index));
  };

  /* T3-129: the proofs ALREADY attached to this payment, for view / reapportion. Available
     because the T3-130 readers now project proof_document_ids onto every payment row. */
  const existingProofIds = useMemo(() => toProofIds(initialPayment), [initialPayment]);
  const docActions = useDocumentActions();

  // T117 multi-currency state: the foreign cash actually moved + the dealt rate
  // (settlement -> contract). The contract-currency equivalent (paymentAmount) is derived.
  const [settlementAmount, setSettlementAmount] = useState(0);
  const [fxRate, setFxRate] = useState('');
  const [fxLoading, setFxLoading] = useState(false);
  const [fxError, setFxError] = useState('');

  // Reset state when the dialog opens with a new schedule row.
  // In reapportion mode, seed from initial* props (existing payment + allocation);
  // otherwise seed from the schedule row defaults.
  useEffect(() => {
    if (!open) return;
    const ei = parseFloat(scheduleRow?.interest_amount) || 0;
    const ec = parseFloat(scheduleRow?.capital_amount) || 0;
    const st = parseFloat(scheduleRow?.scheduled_amount)
      || parseFloat(scheduleRow?.payment_amount)
      || (ei + ec);
    // T3-111: seed from what is still owed, not the scheduled figure — see expectedTotal above.
    const p = partialPaymentInfo(scheduleRow || {});
    const et = p.isPartial ? p.outstanding : st;

    if ((isReapportion || isView) && initialPayment) {
      setPaymentAmount(parseFloat(initialPayment.payment_amount) || 0);
      setPaymentDate(initialPayment.payment_date
        ? format(new Date(initialPayment.payment_date), 'yyyy-MM-dd')
        : format(new Date(), 'yyyy-MM-dd'));
      setPaymentReference(initialPayment.payment_reference || '');
      setPaymentMethod(initialPayment.payment_method || 'EFT');
      setNotes(initialPayment.notes || '');
      // T117: surface the dealt settlement amount + rate read-only on an existing match.
      setSettlementAmount(parseFloat(initialPayment.settlement_amount) || 0);
      setFxRate(initialPayment.fx_rate != null ? String(initialPayment.fx_rate) : '');
    } else {
      setPaymentAmount(isMultiCurrency ? 0 : et);
      setPaymentDate(scheduleRow?.payment_date
        ? format(new Date(scheduleRow.payment_date), 'yyyy-MM-dd')
        : format(new Date(), 'yyyy-MM-dd'));
      setPaymentReference('');
      setPaymentMethod('EFT');
      setNotes('');
      // T117: a fresh multi-currency match starts blank; the rate is auto-suggested by the
      // fetch effect, and T119 auto-fills the settlement amount from the schedule (÷ rate).
      setSettlementAmount(0);
      setFxRate('');
    }
    setProofFiles([]);
    setProofError('');

    if (isReapportion && initialAllocation) {
      setAllocatedInterest(parseFloat(initialAllocation.allocated_interest) || 0);
      setAllocatedCapital(parseFloat(initialAllocation.allocated_capital) || 0);
    } else {
      setAllocatedInterest(ei);
      setAllocatedCapital(ec);
    }
    // Seed the recorded variance reason whenever we're showing an existing match
    // (reapportion edit OR read-only view). In a fresh match there's no prior reason.
    setVarianceReason((isReapportion || isView) ? (initialVarianceReason || '') : '');
    // T3-105: always reopen with the WHT tick-box OFF (see the state declaration).
    setWhtIncluded(false);
    // A freshly-opened dialog holds a seeded amount, not a typed one.
    setCashDirty(false);
    // T3-121: always reopen with carry-forward OFF — moving money between periods rewrites the
    // schedule and must be chosen deliberately, never inherited from the previous row.
    setCarryForward(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scheduleRow]);

  // When the cash amount changes on a row with an active leg pair, default the split
  // interest-first against the new amount. Skipped in reapportion mode since cash is locked there.
  // T3-105: also fires when the WHT tick-box is toggled, so ticking immediately seeds
  // interest/tax and unticking collapses back to an interest-only match.
  useEffect(() => {
    if (isReapportion) return;
    if (matchType !== 'combined' && !whtIncluded) return;
    const cashNow = parseFloat(paymentAmount) || 0;
    if (whtIncluded && matchType === 'payment') {
      // Tax leg first — see seedInterestWhtLegs for why (a short pay must short the INTEREST leg,
      // never the tax leg, because tax cannot be partially remitted and topped up later).
      const legs = seedInterestWhtLegs(cashNow, whtAmount);
      setAllocatedCapital(legs.tax);
      setAllocatedInterest(legs.interest);
    } else {
      const interest = Math.max(0, Math.min(cashNow, expectedInterest));
      setAllocatedInterest(interest);
      setAllocatedCapital(Math.max(0, +(cashNow - interest).toFixed(2)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentAmount, matchType, expectedInterest, whtIncluded, whtAmount]);

  // T3-105: ticking the box means the cash now covers BOTH legs, so the amount field moves from the
  // net figure to net + tax (and back on untick). Only for a fresh single-currency match — never in
  // reapportion/view, where the cash amount is locked to what was actually recorded.
  //
  // ⚠ It must SHIFT the figure, not REPLACE it. Replacing destroyed a deliberate entry: on a short
  // pay of 1,500 against a 1,613.70 line, ticking the box overwrote the 1,500 with the scheduled
  // total. When the operator has typed their own figure (cashDirty) we add/subtract the tax delta,
  // which is the same arithmetic on an untouched field and preserves intent on a dirty one.
  useEffect(() => {
    if (isReapportion || isView || isMultiCurrency || matchType !== 'payment' || !hasWht) return;
    if (cashDirty) {
      setPaymentAmount((prev) => {
        const current = parseFloat(prev) || 0;
        const next = whtIncluded ? current + whtAmount : current - whtAmount;
        return +Math.max(0, next).toFixed(2);
      });
    } else {
      setPaymentAmount(whtIncluded ? +(expectedTotal + whtAmount).toFixed(2) : expectedTotal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whtIncluded]);

  // Multi-currency: Amount Settled and FX Rate are INDEPENDENT inputs. The four variables are
  //   1. Scheduled Amount (contract, fixed)  2. Amount Settled (foreign cash paid)
  //   3. FX Rate (for that settled amount)   4. Payment Amount equivalent = (2) × (3).
  // Only the equivalent is derived (the effect below); neither input recomputes the other.
  // The difference between the equivalent and the Scheduled Amount is the variance.
  // The rate + an on-open settled seed (= scheduled ÷ rate) are set once on open (rate-fetch
  // effect below) purely as a starting convenience.

  // T117: in multi-currency mode the contract-currency equivalent is DERIVED from the foreign
  // settlement amount and the dealt rate; keep paymentAmount in sync so all downstream variance
  // and allocation logic (which reads `cash`) operates on the contract-currency value unchanged.
  useEffect(() => {
    if (!isMultiCurrency || isReapportion || isView) return;
    const s = parseFloat(settlementAmount) || 0;
    const r = parseFloat(fxRate) || 0;
    setPaymentAmount(+(s * r).toFixed(2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settlementAmount, fxRate, isMultiCurrency]);

  // Auto-suggest the dealt rate from the live FX store when the dialog opens for a new
  // multi-currency match. User-overridable (it's an indicative rate, not the dealt rate).
  useEffect(() => {
    if (!open || !isMultiCurrency || isReapportion || isView) return;
    let cancelled = false;
    setFxError('');
    setFxLoading(true);
    apiGet(`/exchange-rates/current/${settlementCurrencyCode}/${currencyCode}`)
      .then((resp) => {
        if (cancelled) return;
        const r = parseFloat(resp?.rate?.rate);
        if (Number.isFinite(r) && r > 0) {
          setFxRate(String(r));
          // Seed Amount Settled = scheduled ÷ rate so the dialog opens on-schedule
          // (equivalent ≈ scheduled, variance ≈ 0). Reciprocal edits thereafter.
          if (expectedTotal > 0) setSettlementAmount(+(expectedTotal / r).toFixed(2));
        } else {
          setFxError(`No FX rate configured for ${settlementCurrencyCode} → ${currencyCode}. Enter the dealt rate manually.`);
        }
      })
      .catch(() => {
        if (!cancelled) setFxError(`Could not load an indicative ${settlementCurrencyCode} → ${currencyCode} rate. Enter the dealt rate manually.`);
      })
      .finally(() => { if (!cancelled) setFxLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isMultiCurrency, settlementCurrencyCode, currencyCode]);

  const cash = parseFloat(paymentAmount) || 0;
  /* T3-105: when the WHT leg is included, the cash covers BOTH legs, so the figure it must be
     compared against is net + tax. Leaving this at the net-only expectedTotal would report a
     false variance of exactly the WHT on every on-schedule combined match, and demand a reason
     for it. The variance still measures cash-vs-obligation — the obligation is simply both legs. */
  const expectedForVariance = whtIncluded ? +(expectedTotal + whtAmount).toFixed(2) : expectedTotal;
  const variance = cash - expectedForVariance;
  const hasVariance = Math.abs(variance) >= 0.01;

  /* T3-121 — CARRY THE SHORTFALL FORWARD.
     Offered only on a genuine SHORT pay (cash below what is owed). Defaults OFF: moving money
     between periods rewrites the schedule and must be a deliberate act, never something an
     operator does by failing to untick a box. */
  const [carryForward, setCarryForward] = useState(false);
  const isShortPay = variance <= -0.01;
  const shortfallAmount = isShortPay ? +Math.abs(variance).toFixed(2) : 0;

  /* Whether the row(s) behind this dialog are the LAST payment marker for their investor. A
     shortfall there has no next row to move to — sp_schedule_apply_override_v2 raises
     'Cannot override the last payment row. Use Finalisation...' — so the option is HIDDEN rather
     than offered and then failed on submit. Fetched on open because the payment feed does not
     carry the flag; null = not yet known, and the option stays hidden until it is. */
  const [lastRowInfo, setLastRowInfo] = useState(null);
  useEffect(() => {
    if (!open || isReapportion || isView) { setLastRowInfo(null); return; }
    const ids = scheduleRow?.isGroup
      ? (scheduleRow.members || []).map((m) => m.id).filter(Boolean)
      : (scheduleRow?.id ? [scheduleRow.id] : []);
    if (ids.length === 0) { setLastRowInfo(null); return; }
    let cancelled = false;
    apiGet(`/payments/settlement-position?schedule_ids=${ids.join(',')}`)
      .then((resp) => {
        if (cancelled) return;
        const rows = resp?.data || [];
        // ANY member being the last row disqualifies the whole group: the amendment is raised per
        // row, so a group containing a final row could only ever be carried in part.
        setLastRowInfo({
          rows,
          anyLast: rows.some((r) => r.is_last_payment_row === true || r.is_last_payment_row === 'true'),
        });
      })
      .catch(() => { if (!cancelled) setLastRowInfo({ rows: [], anyLast: true, failed: true }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scheduleRow]);

  /* Show the option only when it can actually be executed. On a load failure `anyLast` is true, so
     the option hides — the safe direction: the payment still matches, and the shortfall simply
     stays outstanding, which is what would happen anyway. */
  const canCarryForward = isShortPay
    && !isReapportion && !isView
    && matchType !== 'wht'
    && !!lastRowInfo && !lastRowInfo.anyLast;

  /* ------------------------------------------------------------------------------------------
     ALLOCATION — ONE implementation, two configurations.

     Two legs share a single cash amount; editing either rebalances the other. That shape is
     identical for Combined (interest/capital) and for the T3-105 interest+WHT action, so it is
     described by a LEG PAIR rather than duplicated. `allocatedInterest` remains leg A in both
     modes; `allocatedCapital` carries leg B (capital, or WHT) — the state names are kept so the
     Combined submit payload and reapportion seeding are untouched.

     Do NOT add a second allocation block for WHT: MatchDialog is the single shared dialog behind
     Payments / Combined / WHT, and a parallel implementation would drift out of step with this one.
     ------------------------------------------------------------------------------------------ */
  const legPair = useMemo(() => {
    if (matchType === 'combined') {
      return {
        key: 'combined',
        a: { label: 'Allocated to Interest', expected: expectedInterest, firstLabel: 'Interest first' },
        b: { label: 'Allocated to Capital', expected: expectedCapital, firstLabel: 'Capital first' },
        proRata: true,
      };
    }
    if (matchType === 'payment' && whtIncluded) {
      return {
        key: 'wht',
        a: { label: 'to Interest', expected: expectedInterest, firstLabel: 'Interest first' },
        b: { label: 'to Withholding Tax', expected: whtAmount, firstLabel: 'Tax first' },
        // Pro-rata would produce a fractional tax figure, which decision 9 forbids (the WHT leg is
        // full-or-zero until fn_payment_wht_get_pending gains a coverage test). Omitted rather than
        // offered-then-rejected.
        proRata: false,
      };
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchType, whtIncluded, expectedInterest, expectedCapital, whtAmount]);

  /* ------------------------------------------------------------------------------------------
     PER-INVESTOR APPORTIONMENT (T3-109 group match).

     ⚠ This is NOT a second allocation implementation and must never become one. `legPair` above
     stays the SOLE authority on the leg split (interest vs capital, or interest vs tax) of the
     lump sum. This block splits each leg across the N investors who share the contract and due
     date — a DIFFERENT AXIS, composed with legPair rather than competing with it:

         cash (one lump sum)
           └─ legPair splits it:  interest | capital-or-WHT        <- existing, untouched
                └─ this splits each leg across N investors         <- new, derived

     Layout deliberately mirrors FinalisationDialog's Per-Investor Allocation table so the two
     per-investor screens in the product read identically, but it uses the SHARED CurrencyInput
     from components/common (already imported here), not that file's local copies.
     ------------------------------------------------------------------------------------------ */
  const isGroupMatch = scheduleRow?.isGroup === true && !isReapportion && !isView;
  const members = useMemo(
    () => (isGroupMatch ? (scheduleRow.members || []) : []),
    [isGroupMatch, scheduleRow],
  );

  /* Interest is apportioned PRO-RATA on each member's own OUTSTANDING amount (T3-111: what is
     still owed, not the scheduled figure — a member already part-paid must not be asked for the
     cash it has already received). Largest-remainder on the last row so the parts sum EXACTLY to
     the cash: a residual cent has to land somewhere, and silently losing it is not an option.

     WHT is NEVER pro-rata — it is each member's OWN wht_amount. Verified on dev MTN-SZ-2026-0005:
     investor 1 is 5% gross-up (44.56) while investor 10 is 0% (0.00), on the SAME contract and
     date. Apportioning tax by share would invent a liability for an investor who has none and
     under-remit for the one who does. */
  const memberOutstanding = useMemo(
    () => members.map((m) => partialPaymentInfo(m).outstanding),
    [members],
  );
  const memberWht = useMemo(
    () => members.map((m) => parseFloat(m.wht_amount) || 0),
    [members],
  );

  // Shared with the foreign-settlement split below, so both currencies are apportioned by the
  // identical rule and cannot drift a cent apart.
  const apportionInterest = useCallback(
    (total) => apportionByWeight(total, memberOutstanding),
    [memberOutstanding],
  );

  // One row per member: what it owes, and what this payment allocates to it.
  const [investorRows, setInvestorRows] = useState([]);

  // Re-seed whenever the cash, the members, or the WHT tick-box change. The interest leg gets
  // whatever is left after the tax leg, mirroring how legPair splits the same cash.
  useEffect(() => {
    if (!isGroupMatch) { setInvestorRows([]); return; }
    const taxTotal = whtIncluded ? memberWht.reduce((s, v) => s + v, 0) : 0;
    const interestTarget = Math.max(0, cash - taxTotal);
    const parts = apportionInterest(interestTarget);
    setInvestorRows(members.map((m, i) => ({
      schedule_id: m.id,
      investor_id: m.investor_id,
      investor_name: m.investor_name,
      scheduled: parseFloat(m.scheduled_amount) || 0,
      // Cash already received against THIS member before the payment being captured. Same
      // partialPaymentInfo source the queues use, so the modal and the grid cannot disagree.
      matched: partialPaymentInfo(m).matched,
      outstanding: memberOutstanding[i],
      wht_due: memberWht[i],
      wht_rate_applied: m.wht_rate_applied,
      wht_gross_up: m.wht_gross_up,
      alloc_interest: parts[i] || 0,
      alloc_wht: whtIncluded ? memberWht[i] : 0,
    })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGroupMatch, cash, whtIncluded, members]);

  // Editing one member's interest allocation moves ONLY that member — the others are not
  // rebalanced. Deliberate: each member is an independent obligation with its own schedule row,
  // so silently moving another investor's money because this one was corrected would be wrong.
  // The running total against cash is surfaced instead, and blocks submit until it reconciles.
  const handleInvestorAlloc = (index, field, value) => {
    const num = Math.max(0, parseFloat(value) || 0);
    setInvestorRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: num } : row)));
  };

  const setInvestorProRata = () => {
    const taxTotal = whtIncluded ? memberWht.reduce((s, v) => s + v, 0) : 0;
    const parts = apportionInterest(Math.max(0, cash - taxTotal));
    setInvestorRows((prev) => prev.map((row, i) => ({
      ...row,
      alloc_interest: parts[i] || 0,
      alloc_wht: whtIncluded ? row.wht_due : 0,
    })));
  };

  const clearInvestorAlloc = () => setInvestorRows((prev) => prev.map((row) => ({
    ...row, alloc_interest: 0, alloc_wht: 0,
  })));

  const investorAllocTotal = investorRows.reduce(
    (s, r) => s + (r.alloc_interest || 0) + (r.alloc_wht || 0), 0,
  );
  const investorAllocMatches = !isGroupMatch || Math.abs(investorAllocTotal - cash) < 0.01;

  /* Decision 9 extended to the group: a group is only as safe as its worst row. Because
     sp_payment_wht_match rejects a second WHT payment outright, a partial remittance on ANY
     member is unrecoverable — so each member's tax must be its full amount or zero. */
  const investorWhtValid = !isGroupMatch || !whtIncluded
    || investorRows.every((r) => Math.abs(r.alloc_wht - r.wht_due) < 0.01 || r.alloc_wht === 0);

  const handleLegChange = (which, value) => {
    const num = Math.max(0, parseFloat(value) || 0);
    const clamped = Math.min(num, cash);
    if (which === 'a') {
      setAllocatedInterest(clamped);
      setAllocatedCapital(Math.max(0, cash - clamped));
    } else {
      setAllocatedCapital(clamped);
      setAllocatedInterest(Math.max(0, cash - clamped));
    }
  };

  const setLegFirst = (which) => {
    if (!legPair) return;
    const target = Math.max(0, Math.min(cash, legPair[which].expected));
    if (which === 'a') {
      setAllocatedInterest(target);
      setAllocatedCapital(Math.max(0, cash - target));
    } else {
      setAllocatedCapital(target);
      setAllocatedInterest(Math.max(0, cash - target));
    }
  };

  const setProRata = () => {
    if (!legPair || expectedTotal <= 0) return;
    const ratio = legPair.a.expected / expectedTotal;
    const a = +(cash * ratio).toFixed(2);
    setAllocatedInterest(a);
    setAllocatedCapital(+(cash - a).toFixed(2));
  };

  const allocationSumMatches = !legPair
    || Math.abs((allocatedInterest + allocatedCapital) - cash) < 0.01;

  /* Decision 9 — the WHT leg is ALL-OR-NOTHING: the full amount or zero, never a part.
     Interest keeps its T3-103 partial freedom (a short-paid line can be topped up), but
     fn_payment_wht_get_pending has no coverage test, so a partially remitted tax line would
     vanish from the WHT tab with an unreachable remainder. This feature must not create a state
     the system cannot recover from. Lift this once that gap is closed. */
  const whtLegValid = !legPair || legPair.key !== 'wht'
    || Math.abs(allocatedCapital - whtAmount) < 0.01
    || allocatedCapital === 0;
  const reasonRequired = hasVariance;
  const reasonOk = !reasonRequired || varianceReason.trim().length > 0;
  const referenceOk = paymentReference.trim().length > 0;
  // Proof is gated only when this is a new match (reapportion/view don't
  // re-upload). Skip the gate in those modes so existing matches without a
  // proof file remain editable.
  const proofOk = lockMetadata || !proofRequired || proofFiles.length > 0;
  // T117: a new multi-currency match needs a positive settlement amount and dealt rate.
  const settlementOk = !isMultiCurrency || lockMetadata
    || ((parseFloat(settlementAmount) || 0) > 0 && (parseFloat(fxRate) || 0) > 0);
  // T3-105: with the WHT leg included, cash covers BOTH legs, so a zero interest allocation is
  // legitimate (tax remitted, nothing yet to the investor) — the amount itself must still be > 0.
  const isValid = cash > 0
    && allocationSumMatches
    && whtLegValid
    && reasonOk
    && referenceOk
    && !!paymentDate
    && proofOk
    && settlementOk
    // T3-109: on a group match every cent of the lump sum must be attributed to a specific
    // investor's schedule row before it can be written — an unallocated remainder has no row
    // to live on, and a partial WHT leg on any member is unrecoverable.
    && investorAllocMatches
    && investorWhtValid;

  const varianceColor = !hasVariance
    ? 'text.secondary'
    : (variance < 0 ? 'error.main' : 'success.main');
  const varianceLabel = !hasVariance
    ? 'On schedule'
    : `${variance < 0 ? 'Short' : 'Over'} ${currencyCode || ''} ${formatNumber(Math.abs(variance))}`;

  const titleByType = useMemo(() => {
    if (isView) {
      return {
        payment: 'Interest Payment Details',
        combined: 'Combined Payment Details',
        wht: 'Withholding Tax Payment Details',
        fee: 'Broker Fee Payment Details',
      };
    }
    if (isReapportion) {
      return {
        payment: 'Re-apportion Interest Payment',
        combined: 'Re-apportion Combined Payment',
        wht: 'Re-apportion Withholding Tax Payment',
        fee: 'Re-apportion Broker Fee Payment',
      };
    }
    return {
      payment: 'Match Interest Payment',
      combined: 'Match Combined Payment',
      wht: 'Match Withholding Tax Payment',
      fee: 'Match Broker Fee Payment',
    };
  }, [isReapportion, isView]);

  const handleConfirm = () => {
    const payload = {
      payment: {
        payment_amount: +cash.toFixed(2),
        payment_date: paymentDate,
        payment_reference: paymentReference.trim() || null,
        payment_method: paymentMethod,
        notes: notes.trim() || null,
        /* T3-129: a LIST of staged Files (was the single `proof_file`). ⚠ The KEY IS RENAMED, not
           just retyped: `[]` is truthy in JS, so a caller still testing `if (payment.proof_file)`
           would pass with zero files and "upload" nothing. Renaming turns any missed call site
           into a hard undefined failure instead of a silent one. */
        proof_files: proofFiles,
        // T117: when multi-currency, surface the foreign cash + dealt rate so the caller can
        // pass them to the match endpoint. payment_amount stays the contract-currency equivalent.
        ...(isMultiCurrency ? {
          settlement_currency_code: settlementCurrencyCode,
          settlement_amount: +(parseFloat(settlementAmount) || 0).toFixed(2),
          fx_rate: parseFloat(fxRate) || 0,
        } : {}),
      },
      variance: hasVariance
        ? {
            amount: +variance.toFixed(2),
            reason: varianceReason.trim() || null,
          }
        : null,
    };
    if (matchType === 'combined') {
      payload.allocation = {
        allocated_interest: +allocatedInterest.toFixed(2),
        allocated_capital: +allocatedCapital.toFixed(2),
      };
    }
    /* T3-105: the WHT leg. `payment.payment_amount` is overridden to the INTEREST allocation only —
       the interest SP must receive what settles the interest obligation, not the combined cash, or
       it would record the tax as an over-payment of interest and fan the figures out. The tax
       amount travels in its own block and the API writes both in one transaction.
       An interest allocation of 0 is legitimate (tax remitted, nothing yet to the investor): the
       API skips the interest SP entirely and the line stays outstanding on the Interest tab. */
    /* ⚠ `!isGroupMatch` is load-bearing. On a group match the per-investor table below is the
       authority on the tax split (it is per-member and all-or-nothing per member), and it emits
       group.legs[].wht. Emitting this top-level block TOO would put two different tax figures in
       one payload — the API happens to branch isGroup first and ignore this one, but a payload
       that contradicts itself is a defect waiting for the branch order to change. */
    if (matchType === 'payment' && whtIncluded && legPair?.key === 'wht' && !isGroupMatch) {
      payload.payment.payment_amount = +allocatedInterest.toFixed(2);
      /* No proof_files here: one document set covers both legs. The caller uploads
         `payment.proof_files` once and references the same ids on the interest and WHT rows. */
      payload.wht = {
        payment_amount: +allocatedCapital.toFixed(2),
        payment_date: paymentDate,
        payment_reference: paymentReference.trim() || null,
      };
    }
    /* T3-109 group match: ONE lump sum apportioned across the N per-investor schedule rows that
       share this contract and due date. Each leg targets its OWN schedule_id, which is what gives
       per-investor attribution with no schema change — contract_payments has no investor_id, but
       schedule_id IS per-investor on a split contract.

       ⚠ ZERO-ALLOCATION MEMBERS ARE OMITTED, NOT SENT AS 0.00. contract_payments carries
       CHECK (payment_amount > 0), so a zero leg would abort the whole transaction; and a 0.00 row
       is a false settlement record besides. An investor allocated nothing simply gets no row and
       correctly stays outstanding. */
    if (isGroupMatch) {
      const payable = investorRows.filter((r) => r.alloc_interest > 0.005 || r.alloc_wht > 0.005);

      /* T117 + T3-109: ONE payment, MANY investors, ONE currency.
         There is no per-leg dealt rate to capture — there was only ever one deal — so every leg
         carries the SAME fx_rate, and the foreign cash is apportioned across them on the same
         basis (and with the same largest-remainder rule) as the contract-currency amounts. Both
         columns therefore sum back to what was actually paid.
         NOT the full foreign amount on every leg: any report summing settlement_amount would then
         multiply the cash by the number of investors. */
      const fxParts = isMultiCurrency
        ? apportionByWeight(
          parseFloat(settlementAmount) || 0,
          payable.map((r) => (r.alloc_interest || 0) + (r.alloc_wht || 0)),
        )
        : [];

      payload.group = {
        legs: payable.map((r, i) => ({
          schedule_id: r.schedule_id,
          payment_amount: +(r.alloc_interest || 0).toFixed(2),
          /* Amount + rate only. The settlement CURRENCY ID is attached by the caller, which is the
             component that resolved it from the contract — the same division of labour as the
             single-row path, so the dialog never has to know about refdata ids. */
          ...(isMultiCurrency ? {
            settlement_amount: fxParts[i],
            fx_rate: parseFloat(fxRate) || 0,
          } : {}),
          ...(r.alloc_wht > 0.005 ? {
            wht: {
              payment_amount: +r.alloc_wht.toFixed(2),
              payment_date: paymentDate,
              payment_reference: paymentReference.trim() || null,
            },
          } : {}),
        })),
      };
    }
    /* T3-121: request that this payment's shortfall be carried to the next payment. Sent as a flag,
       not an amount — the API re-derives the residual from the schedule AFTER the match commits, so
       the figure can never disagree with what the line actually still owes. */
    if (carryForward && isShortPay) {
      payload.carry_forward_shortfall = true;
    }
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{titleByType[matchType] || 'Match Interest Payment'}</DialogTitle>
      <DialogContent>
        {/* Header — contract / investor / scheduled */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="textSecondary">Contract</Typography>
              <Typography variant="body2" fontWeight="medium">{contractReference || '-'}</Typography>
              {userReference && (
                <Typography variant="caption" color="textSecondary">External Ref: {userReference}</Typography>
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="textSecondary">Investor</Typography>
              <Typography variant="body2" fontWeight="medium">{investorName || '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: matchType === 'combined' ? 3 : 6 }}>
              <Typography variant="caption" color="textSecondary">Scheduled Date</Typography>
              <Typography variant="body2" fontWeight="medium">
                {scheduleRow?.payment_date ? format(new Date(scheduleRow.payment_date), 'dd MMM yyyy') : '-'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: matchType === 'combined' ? 3 : 6 }}>
              {/* T3-111: on a part-paid line the label and the figure both change — this is the
                  OUTSTANDING balance, not the scheduled amount, and the difference is exactly
                  the cash an operator would otherwise have paid twice. */}
              <Typography variant="caption" color="textSecondary">
                {partial.isPartial ? 'Outstanding Balance' : 'Scheduled Amount'}
              </Typography>
              <Typography variant="body2" fontWeight="bold" color={partial.isPartial ? 'warning.main' : 'inherit'}>
                {currencyCode} {formatNumber(expectedTotal)}
              </Typography>
              {partial.isPartial && (
                <Typography variant="caption" color="textSecondary" display="block">
                  {formatNumber(partial.matched)} already received of {formatNumber(partial.scheduled)}
                </Typography>
              )}
            </Grid>
            {matchType === 'combined' && (
              <>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="textSecondary">Expected Interest</Typography>
                  <Typography variant="body2">{currencyCode} {formatNumber(expectedInterest)}</Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="textSecondary">Expected Capital</Typography>
                  <Typography variant="body2">{currencyCode} {formatNumber(expectedCapital)}</Typography>
                </Grid>
              </>
            )}
            {/* T3-105 — WHT context on an INTEREST match. The scheduled amount is the NET figure,
                which previously gave no clue that tax was deducted or that a second leg was owed.
                Shows the gross it came out of, the tax, the net payable, and whether the tax has
                been remitted. Read-only: it never moves the cash amount or the variance. */}
            {matchType === 'payment' && hasWht && (
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 600 }}>
                    Withholding Tax
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={whtGrossUp ? 'secondary' : (whtAlreadyPaid ? 'success' : 'warning')}
                    label={whtGrossUp
                      ? 'Grossed up — company expense'
                      : (whtAlreadyPaid ? 'Remitted' : 'Not yet remitted')}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="textSecondary">Gross Interest</Typography>
                  <Typography variant="body2">{currencyCode} {formatNumber(grossInterest)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="textSecondary">
                    WHT{whtRate != null ? ` @ ${(parseFloat(whtRate) * (whtRate > 1 ? 1 : 100)).toFixed(2)}%` : ''}
                    {whtGrossUp ? ' (Company Expense)' : ''}
                  </Typography>
                  {/* Gross-up: the payer absorbs the tax, so nothing is deducted from the investor —
                      shown plain, NOT in the red parentheses that mean "deducted". */}
                  <Typography variant="body2" sx={{ color: whtGrossUp ? 'secondary.main' : 'error.main' }}>
                    {whtGrossUp
                      ? `${currencyCode} ${formatNumber(whtAmount)}`
                      : `(${currencyCode} ${formatNumber(whtAmount)})`}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, pt: 0.5, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="body2" fontWeight="medium">Net Payable</Typography>
                  <Typography variant="body2" fontWeight="bold">{currencyCode} {formatNumber(expectedTotal)}</Typography>
                </Box>

                {/* T3-105 — VIEW MODE: what was actually remitted, not merely what was owed. Sourced
                    from the already-loaded matched-WHT list (joined on the schedule_id the two legs
                    share), so the record reflects how the payment was captured. Where the remitted
                    amount differs from the scheduled tax the difference is called out rather than
                    left for the reader to spot. */}
                {isView && whtSettlement && (
                  <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="textSecondary">Remitted</Typography>
                      <Typography variant="body2">
                        {currencyCode} {formatNumber(whtSettlement.amount)}
                        {whtSettlement.date ? ` on ${formatShortDate(whtSettlement.date)}` : ''}
                      </Typography>
                    </Box>
                    {whtSettlement.reference && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="textSecondary">Reference</Typography>
                        <Typography variant="body2">{whtSettlement.reference}</Typography>
                      </Box>
                    )}
                    {whtRemittanceDiff !== 0 && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'error.main' }}>
                        {whtRemittanceDiff > 0 ? 'Over' : 'Short'} by {currencyCode}{' '}
                        {formatNumber(Math.abs(whtRemittanceDiff))} against the scheduled withholding tax.
                      </Typography>
                    )}
                  </Box>
                )}
                {whtGrossUp && (
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.75 }}>
                    The investor receives the full gross interest; the withholding tax is borne by the
                    company and remitted separately, so Net Payable equals Gross Interest.
                  </Typography>
                )}
                {whtAlreadyPaid && !whtGrossUp && (
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.75 }}>
                    The tax leg was matched on the Withholding Tax tab. Only the interest payment is
                    recorded here.
                  </Typography>
                )}
                {isMultiCurrency && !whtAlreadyPaid && (
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.75 }}>
                    Withholding tax for this contract is recorded on the Withholding Tax tab.
                  </Typography>
                )}
              </Grid>
            )}
            {matchType === 'wht' && whtContext && (
              <>
                <Grid size={{ xs: 6, sm: 4 }}>
                  <Typography variant="caption" color="textSecondary">Gross Interest</Typography>
                  <Typography variant="body2">{currencyCode} {formatNumber(whtContext.gross_interest)}</Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 4 }}>
                  <Typography variant="caption" color="textSecondary">WHT Rate</Typography>
                  <Typography variant="body2">
                    {whtContext.wht_rate != null ? `${(parseFloat(whtContext.wht_rate) * (whtContext.wht_rate > 1 ? 1 : 100)).toFixed(2)}%` : '-'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 4 }}>
                  <Typography variant="caption" color="textSecondary">WHT Due</Typography>
                  <Typography variant="body2" fontWeight="medium">{currencyCode} {formatNumber(whtContext.wht_amount)}</Typography>
                </Grid>
              </>
            )}
          </Grid>
        </Paper>

        {/* T117: multi-currency settlement row — foreign cash + dealt rate. The contract-
            currency equivalent (Payment Amount) is derived and shown read-only beneath. */}
        {isMultiCurrency && (
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CurrencyInput
                label={`Amount ${matchType === 'wht' ? 'Paid' : (scheduleRow?.capital_amount > 0 && !(scheduleRow?.interest_amount > 0) ? 'Paid' : 'Settled')} (${settlementCurrencyCode})`}
                value={settlementAmount}
                onChange={(v) => setSettlementAmount(v)}
                required
                disabled={lockMetadata}
                helperText={lockMetadata ? 'Foreign cash settled (locked).' : `Cash actually moved in ${settlementCurrencyCode}`}
                sx={{ '& input': { textAlign: 'right' } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={`FX Rate (${settlementCurrencyCode}/${currencyCode})`}
                value={fxRate}
                onChange={(e) => setFxRate(e.target.value.replace(/[^0-9.]/g, ''))}
                fullWidth
                required
                disabled={lockMetadata}
                error={!!fxError}
                helperText={
                  fxLoading ? 'Loading indicative rate…'
                    : (fxError || (lockMetadata ? 'Dealt rate (locked).' : `Dealt rate; pre-filled from system rate, override with the actual rate. 1 ${settlementCurrencyCode} = ? ${currencyCode}`))
                }
                inputProps={{ inputMode: 'decimal' }}
                sx={{ '& input': { textAlign: 'right' } }}
              />
            </Grid>
          </Grid>
        )}

        {/* Payment amount on its own row so the Allocation section can sit
            directly beneath it for Combined matches. In multi-currency mode this is the
            DERIVED contract-currency equivalent (settlement × rate) and is read-only. */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <CurrencyInput
              /* T3-105: with the WHT leg included the cash covers BOTH legs, so the label changes.
                 A field reading "Payment Amount" while holding interest-plus-tax is the kind of
                 quiet ambiguity that produces a mis-keyed figure. */
              label={isMultiCurrency
                ? `Payment Amount (${currencyCode}, equivalent)`
                : (whtIncluded ? 'Total Paid' : 'Payment Amount')}
              value={paymentAmount}
              /* Mark the field dirty so the WHT tick-box shifts this figure rather than
                 overwriting it — see the cashDirty declaration. */
              onChange={(v) => { setCashDirty(true); setPaymentAmount(v); }}
              required
              disabled={lockMetadata || isMultiCurrency}
              helperText={
                isMultiCurrency
                  ? (() => {
                      const r = parseFloat(fxRate) || 0;
                      const s = parseFloat(settlementAmount) || 0;
                      return r > 0 && s > 0
                        ? `≈ ${currencyCode} ${formatNumber(s * r)} at ${settlementCurrencyCode}/${currencyCode} ${formatNumber(r, 6)}`
                        : `Equivalent in ${currencyCode}, computed from the settlement amount × rate`;
                    })()
                  : (isView ? 'Read-only.' : (isReapportion ? 'Cash amount is locked; reverse the match to change it.' : undefined))
              }
              sx={{ '& input': { textAlign: 'right' } }}
            />
          </Grid>
        </Grid>

        {/* T3-105 — record the WHT remittance in the same action. Offered only on an interest
            match that actually carries WHT, whose tax leg is still outstanding, and in a single
            currency (decision 8: multi-currency WHT stays on its own tab, where the FX fields
            already work). Never pre-ticked — see the whtIncluded state. */}
        {matchType === 'payment' && hasWht && !whtAlreadyPaid && !isMultiCurrency && !lockMetadata && (
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={whtIncluded}
                  onChange={(e) => setWhtIncluded(e.target.checked)}
                  size="small"
                />
              )}
              label={(
                <Box>
                  <Typography variant="body2">
                    Also record the withholding tax remittance of{' '}
                    <strong>{currencyCode} {formatNumber(whtAmount)}</strong>
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Leave unticked to record the interest payment only — the tax stays outstanding
                    on the Withholding Tax tab.
                  </Typography>
                </Box>
              )}
              /* Negative margin cancels the Checkbox's own padding so the control sits on the same
                 left edge as the outlined inputs above and below it, rather than a few px inboard. */
              sx={{ alignItems: 'flex-start', ml: -1.125, mr: 0, '& .MuiCheckbox-root': { pt: 0 } }}
            />
          </Box>
        )}

        {/* Allocation — ONE block, two configurations (Combined interest/capital, or the T3-105
            interest/WHT pair). Driven entirely by legPair; see its definition for why this is not
            duplicated per mode. */}
        {legPair && (
          <Box sx={{ mt: 2, pb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">Allocation</Typography>
              {!lockAllocation && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="outlined" onClick={() => setLegFirst('a')}>{legPair.a.firstLabel}</Button>
                  <Button size="small" variant="outlined" onClick={() => setLegFirst('b')}>{legPair.b.firstLabel}</Button>
                  {legPair.proRata && (
                    <Button size="small" variant="outlined" onClick={setProRata}>Pro-rata</Button>
                  )}
                </Box>
              )}
            </Box>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <CurrencyInput
                  label={legPair.a.label}
                  value={allocatedInterest}
                  onChange={(v) => handleLegChange('a', v)}
                  disabled={lockAllocation}
                  sx={{ '& input': { textAlign: 'right' } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <CurrencyInput
                  label={legPair.b.label}
                  value={allocatedCapital}
                  onChange={(v) => handleLegChange('b', v)}
                  disabled={lockAllocation}
                  error={!whtLegValid}
                  helperText={!whtLegValid
                    ? `Withholding tax must be paid in full (${formatNumber(whtAmount)}) or left for later — a partial remittance cannot currently be topped up.`
                    : undefined}
                  sx={{ '& input': { textAlign: 'right' } }}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* T3-109 — PER-INVESTOR ALLOCATION.
            Shown only for a grouped multi-investor line. This splits the lump sum ACROSS
            INVESTORS; the legPair block above splits it ACROSS LEGS. Two axes, composed —
            see the isGroupMatch block for why this is not a second allocation implementation. */}
        {isGroupMatch && (
          <Box sx={{ mt: 2, pb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">Per-Investor Allocation</Typography>
              <ButtonGroup size="small" variant="outlined">
                <Button onClick={setInvestorProRata}>Pro-rata</Button>
                <Button onClick={clearInvestorAlloc}>Clear</Button>
              </ButtonGroup>
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    {/* Same accounting triple as the payment queues, so a line reads identically
                        whichever screen it is on. "Allocated" keeps its GRID meaning — cash already
                        received — and the input is "Allocating now", because one word cannot carry
                        both meanings in one table without making the totals row unreadable. */}
                    <TableCell>Investor</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Allocated</TableCell>
                    <TableCell align="right">Outstanding</TableCell>
                    {whtIncluded && <TableCell align="right">Tax Due</TableCell>}
                    <TableCell align="right" sx={{ minWidth: 150 }}>Allocating now</TableCell>
                    {whtIncluded && <TableCell align="right" sx={{ minWidth: 150 }}>Allocated WHT</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {investorRows.map((row, idx) => {
                    const partOfIt = row.outstanding < row.scheduled - 0.01;
                    return (
                      <TableRow key={row.schedule_id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {row.investor_name || `Investor ${row.investor_id ?? idx + 1}`}
                          </Typography>
                          {/* Same wording as FinalisationDialog so the two per-investor
                              screens describe the same thing identically. */}
                          {parseFloat(row.wht_rate_applied) > 0 && (
                            <Typography variant="caption" color="textSecondary" display="block">
                              WHT: {parseFloat(row.wht_rate_applied).toFixed(2)}%
                              {row.wht_gross_up ? ' (gross-up)' : ''}
                            </Typography>
                          )}
                        </TableCell>
                        {/* Amount / Allocated / Outstanding as their own columns. NO strike-through:
                            a struck figure reads as "void", when the scheduled amount is a perfectly
                            valid fact that simply is not what is owed today. State both plainly. */}
                        <TableCell align="right">
                          <Typography variant="body2" color={partOfIt ? 'textSecondary' : 'inherit'}>
                            {formatNumber(row.scheduled)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color={row.matched > 0 ? 'success.main' : 'textSecondary'}>
                            {row.matched > 0 ? formatNumber(row.matched) : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium" color={partOfIt ? 'warning.main' : 'inherit'}>
                            {formatNumber(row.outstanding)}
                          </Typography>
                        </TableCell>
                        {whtIncluded && (
                          <TableCell align="right" sx={{ color: row.wht_due > 0 ? 'error.main' : 'text.secondary' }}>
                            {row.wht_due > 0 ? `(${formatNumber(row.wht_due)})` : '-'}
                          </TableCell>
                        )}
                        <TableCell align="right">
                          <CurrencyInput
                            size="small"
                            value={row.alloc_interest}
                            onChange={(v) => handleInvestorAlloc(idx, 'alloc_interest', v)}
                            sx={{ '& input': { textAlign: 'right' } }}
                          />
                        </TableCell>
                        {whtIncluded && (
                          <TableCell align="right">
                            <CurrencyInput
                              size="small"
                              value={row.alloc_wht}
                              onChange={(v) => handleInvestorAlloc(idx, 'alloc_wht', v)}
                              error={!(Math.abs(row.alloc_wht - row.wht_due) < 0.01 || row.alloc_wht === 0)}
                              sx={{ '& input': { textAlign: 'right' } }}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                  {/* Foots in EVERY money column, so the table reconciles across the page rather
                      than only under the input. */}
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell><Typography variant="body2" fontWeight="bold">Total</Typography></TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">
                        {formatNumber(investorRows.reduce((s, r) => s + (r.scheduled || 0), 0))}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold" color="success.main">
                        {formatNumber(investorRows.reduce((s, r) => s + (r.matched || 0), 0))}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">
                        {formatNumber(investorRows.reduce((s, r) => s + r.outstanding, 0))}
                      </Typography>
                    </TableCell>
                    {whtIncluded && (
                      <TableCell align="right" sx={{ color: 'error.main' }}>
                        <Typography variant="body2" fontWeight="bold">
                          ({formatNumber(investorRows.reduce((s, r) => s + r.wht_due, 0))})
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">
                        {formatNumber(investorRows.reduce((s, r) => s + (r.alloc_interest || 0), 0))}
                      </Typography>
                    </TableCell>
                    {whtIncluded && (
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {formatNumber(investorRows.reduce((s, r) => s + (r.alloc_wht || 0), 0))}
                        </Typography>
                      </TableCell>
                    )}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Every cent must land on a specific investor's schedule row before this can be
                written — an unallocated remainder has no row to live on. */}
            <Alert
              severity={investorAllocMatches ? 'success' : 'warning'}
              sx={{ mt: 1, py: 0.25 }}
            >
              {investorAllocMatches
                ? `Allocated in full — ${currencyCode} ${formatNumber(investorAllocTotal)}`
                : `${investorAllocTotal > cash ? 'Over-allocated by' : 'Unallocated:'} ${currencyCode} ${formatNumber(Math.abs(cash - investorAllocTotal))} (${formatNumber(investorAllocTotal)} of ${formatNumber(cash)})`}
            </Alert>
            {!investorWhtValid && (
              <Alert severity="error" sx={{ mt: 1, py: 0.25 }}>
                Each investor&apos;s withholding tax must be paid in full or left for later — a
                partial remittance cannot currently be topped up.
              </Alert>
            )}
            {/* Zero-allocation members write no row at all; say so rather than letting the
                operator discover it afterwards. */}
            {investorRows.some((r) => (r.alloc_interest || 0) <= 0.005 && (r.alloc_wht || 0) <= 0.005) && (
              <Alert severity="info" sx={{ mt: 1, py: 0.25 }}>
                {investorRows.filter((r) => (r.alloc_interest || 0) <= 0.005 && (r.alloc_wht || 0) <= 0.005).length}
                {' '}investor(s) allocated nothing — no payment is recorded for them and their
                line stays outstanding.
              </Alert>
            )}
          </Box>
        )}

        {/* Remaining payment metadata — 3-column row: date / reference / method. */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Payment Date"
              type="date"
              fullWidth
              required
              disabled={lockMetadata}
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Payment Reference"
              fullWidth
              required
              disabled={lockMetadata}
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <PaymentMethodSelect value={paymentMethod} onChange={setPaymentMethod} disabled={lockMetadata} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Notes"
              fullWidth
              multiline
              minRows={2}
              disabled={lockMetadata}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Grid>
          {/* Read-only "Matched by" — the user who processed the match. View mode only. */}
          {isView && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Matched by"
                fullWidth
                disabled
                value={initialPayment?.matched_by || '-'}
              />
            </Grid>
          )}
          {!lockMetadata && (
            <Grid size={{ xs: 12 }}>
              {/* T3-129: several documents, one dropzone + a removable list. */}
              <ProofUploadField
                multiple
                files={proofFiles}
                onFilesChange={acceptProofFiles}
                onRemoveAt={removeProofFileAt}
                label={proofLabel}
                error={proofError || undefined}
                helperText={proofError ? undefined : `PDF, image, Word or Excel. Max 10 MB each, up to ${MAX_PROOF_FILES} documents.`}
              />
            </Grid>
          )}
          {/* T3-129: view / reapportion previously showed NO proof at all — the field was gated
              behind !lockMetadata, so an operator could not see the document for the payment they
              were looking at. Read-only here: reapportion does not touch the proof. */}
          {lockMetadata && existingProofIds.length > 0 && (
            <Grid size={{ xs: 12 }}>
              <ExistingProofList
                ids={existingProofIds}
                docActions={docActions}
                label={matchType === 'wht' ? 'Proof of WHT Payment' : 'Proof of Payment'}
              />
            </Grid>
          )}
        </Grid>

        {/* Variance — always rendered to keep the dialog height stable. The
            reason field is disabled when cash matches expected. */}
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2">Variance</Typography>
          <Chip
            size="small"
            label={varianceLabel}
            color={!hasVariance ? 'default' : (variance < 0 ? 'error' : 'success')}
            variant="outlined"
          />
        </Box>
        <TextField
          label="Variance reason"
          fullWidth
          multiline
          minRows={2}
          required={hasVariance}
          disabled={isView || !hasVariance}
          // In view mode always surface the reason that was recorded at match time,
          // independent of the live cash-vs-expected recompute (which can differ from
          // the amounts as matched, or read zero when the schedule row can't be resolved).
          // In match/reapportion mode keep the live-variance gating.
          value={isView ? varianceReason : (hasVariance ? varianceReason : '')}
          onChange={(e) => setVarianceReason(e.target.value)}
          placeholder={
            isView
              ? (varianceReason ? '' : 'No variance reason recorded.')
              : (hasVariance ? '' : 'No variance — cash matches the scheduled amount.')
          }
          helperText={(!isView && hasVariance) ? 'Required when the cash received differs from the scheduled amount (e.g. counterparty schedule, fee deduction, FX shortfall).' : ' '}
        />

        {/* T3-121 — carry the shortfall to the next payment.
            Only on a genuine short pay, and only where there IS a next payment row to carry to. */}
        {canCarryForward && (
          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={carryForward}
                  onChange={(e) => setCarryForward(e.target.checked)}
                  size="small"
                />
              )}
              label={(
                <Box>
                  <Typography variant="body2">
                    Carry the {currencyCode} {formatNumber(shortfallAmount)} shortfall to the next payment
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Leave unticked to leave the shortfall outstanding on this line.
                  </Typography>
                </Box>
              )}
            />
            {carryForward && (
              /* Say plainly that this does NOT take effect on save. The payment matches now, but the
                 schedule change needs approval — so the line stays outstanding in the meantime and
                 will carry a "Carry-forward pending" marker. Without this the operator would expect
                 the row to disappear and think the option failed. */
              <Alert severity="info" sx={{ mt: 1, py: 0.25 }}>
                The payment is matched immediately, but moving the shortfall changes the schedule, so
                it is <strong>submitted for approval</strong>. This line stays outstanding — marked
                “Carry-forward pending” — until an approver applies it.
              </Alert>
            )}
          </Box>
        )}
        {/* The option is deliberately absent on the final payment row: there is no later row to
            carry to, and any remaining shortfall is settled through Finalisation. Saying so is
            better than silently omitting a control the operator may be looking for. */}
        {isShortPay && !isReapportion && !isView && matchType !== 'wht'
          && lastRowInfo && lastRowInfo.anyLast && !lastRowInfo.failed && (
          <Alert severity="info" sx={{ mt: 1, py: 0.25 }}>
            This is the last scheduled payment, so a shortfall cannot be carried forward. Any
            residual is settled through contract Finalisation.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {isView ? (
          <Button variant="contained" onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button variant="contained" onClick={handleConfirm} disabled={!isValid || submitting}>
              {submitting
                ? (isReapportion ? 'Saving…' : 'Recording…')
                : (isReapportion ? 'Save Changes' : 'Confirm Match')}
            </Button>
          </>
        )}
      </DialogActions>
      {/* T3-129: previewing an already-attached proof from view/reapportion mode. */}
      <DocumentPreviewDialog {...docActions} />
    </Dialog>
  );
};

export default MatchDialog;
